<template>
  <DialogLayout :class-name="'create-account-dialog'" @close="handleClose">
    <template #title>{{ props.title || (props.id ? t('account.createDialog.editTitle') : t('account.createDialog.title')) }}</template>
    <template #default>
      <el-form label-position="top" :model="formData" :rules="rules" ref="formRef">
        <!-- Email input - always visible -->
        <el-form-item :label="t('account.createDialog.email')" prop="email" :style="{
          marginBottom: emailErrorBottom
        }">
          <div class="email-input-wrapper" ref="emailInputWrapperRef">
            <el-input
              v-model="emailInput"
              :placeholder="t('account.createDialog.emailPlaceholder')"
              :disabled="!!props.id"
              @blur="handleEmailBlur"
              @focus="handleEmailFocus"
            />
          </div>
        </el-form-item>

        <!-- Email suggestions dropdown - using Teleport -->
        <Teleport to="body">
          <div
            v-if="showSuggestions && emailSuggestions.length > 0"
            class="email-suggestions-teleport"
            :style="suggestionStyle"
            @mousedown.prevent="handleSuggestionMouseDown"
          >
            <div
              v-for="suggestion in emailSuggestions"
              :key="suggestion.id"
              class="suggestion-item"
              @click="handleSelectSuggestion(suggestion)"
            >
              {{ suggestion.email }}
            </div>
          </div>
        </Teleport>

        <!-- Username input - shown after email is selected/validated -->
        <el-form-item v-if="showFullForm" :label="t('account.createDialog.username')" prop="username" required>
          <el-input
            v-model="formData.username"
            :placeholder="t('account.createDialog.usernamePlaceholder')"
          />
        </el-form-item>

        <!-- Role selector - shown after email is selected/validated -->
        <el-form-item v-if="showFullForm" prop="roleId" :label="t('account.createDialog.role')" required>
          <el-select v-model="formData.roleId" :placeholder="t('account.createDialog.rolePlaceholder')" style="width: 100%">
            <el-option
              v-for="role in roleList"
              :key="role.id"
              :label="getRoleLabel(role.id)"
              :value="role.id"
            />
          </el-select>
        </el-form-item>

      </el-form>
    </template>
    <template #footer>
      
      <el-button type="default" @click="handleCancel">{{ t('account.createDialog.cancel') }} </el-button>
      <el-button type="primary" :disabled="!canSave" @click="handleConfirm">{{ props.id ? t('common.save') : t('account.createDialog.confirm') }}</el-button>
    </template>
  </DialogLayout>
</template>

<script setup lang="ts">
import { reactive, ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { FormInstance, FormRules } from 'element-plus';
import DialogLayout from '@/components/business-components/dialog/dialog-layout.vue';
import {
  getEmailSuggestions,
  getAccountNameByEmail,
  getRoles
} from '@/api/account'
import { useUserStore } from '@/store/modules/user';
import { ElMessage } from 'element-plus';
import { ProductId } from '../../../enum/account';
import { SystemCanCreateRole } from '../../../router/config/roles';
const { t } = useI18n();
const userStore = useUserStore();

interface Props {
  title?: string;
  username?: string;
  email?: string;
  department?: string;
  roleId?: number;
  id?: string | number;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  username: '',
  email: '',
  department: '',
  roleId: undefined,
  id: undefined,
});

const emit = defineEmits<{
  cancel: [];
  confirm: [data: any];
  close: [];
}>();

const formRef = ref<FormInstance>();
const emailInputWrapperRef = ref<HTMLElement>();

// Email input and suggestions
const emailInput = ref('');
const emailSuggestions = ref<Account.EmailSuggestionItem[]>([]);
const showSuggestions = ref(false);
const emailExists = ref(false);
const showFullForm = ref(false);
const roleList = ref<Account.RoleInfo[]>([]);
const suggestionStyle = ref({});
const hasCalculatedPosition = ref(false); // 标记是否已计算过位置
const isSelectingSuggestion = ref(false); // 标记是否正在选择建议
const resolvedEmail = ref(''); // 已经回显过用户名的邮箱，避免重复校验时覆盖用户手动输入
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formData = reactive({
  email: '',
  username: '',
  roleId: undefined as number | undefined
});
const emailErrorBottom = ref('20px')
const canSave = computed(() => {
  if (formData.email && formData.username && formData.roleId) {
    return true;
  }
  return false;
})
const rules: FormRules = {
  username: [
    { required: true, message: t('account.createDialog.validation.usernameRequired'), trigger: 'blur' },

  ],
  email: [
    {
      required: true,
      validator: async (rule: any, value: any, callback: any) => {
        // 编辑模式下跳过验证
        if (props.id) {
          return callback();
        }

        // 1. 必填校验
        if (!emailInput.value) {
          return callback(new Error(t('account.createDialog.validation.emailRequired')));
        }

        // 2. 格式校验
        const email = String(emailInput.value).trim();
        if (!EMAIL_REGEX.test(email)) {
          return callback(new Error(t('account.createDialog.validation.emailInvalid')));
        }

        // 一个账号可以接入多个企业，邮箱在别处已存在不再是新增账号的阻断条件，
        // 故不再调 check-email 预校验；同企业内重复由后端在提交时返回错误
        formData.email = email;
        await fillUsernameByEmail(email);
        showFullForm.value = true;
        emailErrorBottom.value = '20px'
        callback();
      },
      trigger: 'blur'
    },
  ],
  roleId: [
    { required: true, message: t('account.createDialog.validation.roleRequired'), trigger: 'change' }
  ]
};

// 同一个账号全局只有一个用户名、密码（不同企业下的角色才可以不同），
// 所以邮箱确定后要回显该账号已有的用户名；查不到则是新注册用户，用户名留空
const fillUsernameByEmail = async (email: string) => {
  if (resolvedEmail.value === email) {
    // 同一邮箱重复校验（如提交时的整表校验）不再覆盖用户已手动修改的用户名
    return;
  }
  try {
    const account = await getAccountNameByEmail({ email });
    formData.username = account?.name || '';
    resolvedEmail.value = email;
  } catch (error) {
    console.error('Error fetching account name by email:', error);
    // 查询失败时无法判断归属，清空避免把上一个邮箱的用户名带到新邮箱上
    formData.username = '';
    resolvedEmail.value = '';
  }
};

// 邮箱格式合法就去查一次用户名：不能只依赖 blur，
// 改邮箱后 blur 未必再次触发校验，否则会一直留着上一个邮箱回显的用户名
const resolveUsernameByEmail = (value: string) => {
  const email = String(value || '').trim();
  if (!EMAIL_REGEX.test(email)) {
    return;
  }
  fillUsernameByEmail(email);
};

// Update suggestion position (只在首次调用时计算)
const updateSuggestionPosition = () => {
  if (!emailInputWrapperRef.value || hasCalculatedPosition.value) return;

  const rect = emailInputWrapperRef.value.getBoundingClientRect();
  suggestionStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    zIndex: 9999
  };
  hasCalculatedPosition.value = true;
};

// Handle email search
const searchEmailSuggestions = async (value: string) => {
  emailExists.value = false;

  if (!value || value.length < 1) {
    showSuggestions.value = false;
    emailSuggestions.value = [];
    return;
  }

  try {
    // Call getEmailSuggestions
    let productId = useUserStore().productId as number;
    const suggestions = await getEmailSuggestions({
      emailKeyword: value,
      productId: productId
    });

    if (suggestions && suggestions.length > 0) {
      emailSuggestions.value = suggestions;
      updateSuggestionPosition();
      showSuggestions.value = true;
    } else {
      // If no suggestions found
      emailSuggestions.value = [];
      showSuggestions.value = false;
      // 邮箱格式校验通过后（blur 触发）会展开完整表单
    }
  } catch (error) {
    console.error('Error fetching email suggestions:', error);
  }
};

// Watch emailInput changes with debounce
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
watch(() => emailInput.value, (newValue) => {

  if (props.id) {
    // 编辑时不查询
    return;
  }
  // 如果是选择建议触发的变化，跳过查询
  if (isSelectingSuggestion.value) {
    isSelectingSuggestion.value = false;
    return;
  }

  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    searchEmailSuggestions(newValue);
    resolveUsernameByEmail(newValue);
  }, 500);
});

// Handle email focus
const handleEmailFocus = () => {
  // 不自动显示建议，只在输入变化时才显示
  // 这样选择建议后再次聚焦不会显示旧的建议
  if (emailSuggestions.value && emailSuggestions.value.length) {
    showSuggestions.value = true;
  }
};

// Handle email blur
const handleEmailBlur = async () => {
  // Hide suggestions after a short delay to allow click events
  setTimeout(() => {
    showSuggestions.value = false;
  }, 200);
};

// Prevent blur when clicking on suggestions
const handleSuggestionMouseDown = (e: MouseEvent) => {
  // This prevents the blur event from firing
  e.preventDefault();
};

// Handle selecting a suggestion
const handleSelectSuggestion = async (suggestion: Account.EmailSuggestionItem) => {
  // 设置标志，防止 watch 触发查询
  isSelectingSuggestion.value = true;

  emailInput.value = suggestion.email;
  formData.email = suggestion.email;
  formData.username = suggestion.name;
  resolvedEmail.value = suggestion.email;
  showSuggestions.value = false;
  emailSuggestions.value = []; // 清空建议列表
  emailExists.value = false;
  showFullForm.value = true;

  // Auto-select first role if available
  if (roleList.value.length > 0 && !formData.roleId) {
    formData.roleId = roleList.value[0].id;
  }
};

// Get role label from i18n
const getRoleLabel = (roleId: number): string => {
  return t(`account.roleNames.${roleId}`);
};

// Fetch roles on mount
onMounted(async () => {
  try {
    const rolesConfig = await getRoles();
    const roles = rolesConfig[0]?.roles || [];
    // Filter to only show roles: 4 (Recruitment Manager), 5 (Recruitment Specialist), 6 (Interviewer)
    let curSystem = userStore.userSystem;
    let systemRoles = SystemCanCreateRole[curSystem] || [];
    roleList.value = roles.filter(role => systemRoles.includes(role.id));

    // Initialize form with props if provided (edit mode)
    if (props.id) {
      // Edit mode - initialize form with provided data
      emailInput.value = props.email || '';
      formData.email = props.email || '';
      formData.username = props.username || '';
      formData.roleId = props.roleId;
      resolvedEmail.value = props.email || '';
      showFullForm.value = true; // Show full form immediately in edit mode
    } else {
      // Create mode - set default role to first one
      if (roleList.value.length > 0) {
        formData.roleId = roleList.value[0].id;
      }
    }
  } catch (error) {
    console.error('Error fetching roles:', error);
    ElMessage.error(t('account.createDialog.fetchRolesError'));
  }
});

const handleCancel = () => {
  emit('cancel');
};

const handleConfirm = async () => {
  if (!formRef.value) return;

  await formRef.value.validate((valid) => {
    if (valid && showFullForm.value) {
      emit('confirm', {
        email: formData.email,
        name: formData.username,
        roleId: formData.roleId,
        position: formData.username, // Use username as position for now
      });
    }
  });
};

const handleClose = () => {
  emit('close');
};
</script>

<style lang="scss" scoped>
.create-account-dialog {
  min-width: 592px;
  max-width: 592px;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.email-input-wrapper {
  position: relative;
  width: 100%;
}

.error-text {
  font-size: 14px;
  color: #F56C6C;
  margin-top: 4px;
  line-height: 20px;
}

:deep(.el-form-item) {
  margin-bottom: 20px;
}

:deep(.el-form-item__label) {
  font-size: 14px;
  color: #262626;
  font-weight: 500;
  margin-bottom: 8px;
}

:deep(.el-input__inner) {
  height: 35px;
}

:deep(.el-select) {
  width: 100%;
}
</style>

<style lang="scss">
/* Global styles for teleported suggestions (not scoped) */
.email-suggestions-teleport {
  background: #ffffff;
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;

  .suggestion-item {
    padding: 10px 16px;
    cursor: pointer;
    font-size: 14px;
    color: #606266;
    transition: background-color 0.3s;

    &:hover {
      background-color: #F5F7FA;
    }

    &:first-child {
      color: #6366F1;
      font-weight: 500;
    }
  }
}
</style>
